import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLACEHOLDER =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'><rect width='200' height='200' fill='%23d1fae5'/><circle cx='100' cy='78' r='34' fill='%23059669'/><path d='M40 180c0-33 27-60 60-60s60 27 60 60z' fill='%23059669'/></svg>";

const Staff = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/staff`);
        if (active) setStaff(data || []);
      } catch (e) {
        if (active) setError('Unable to load staff at the moment.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // Sort by display_order first (allows manual ordering within a department like Leadership),
  // then alphabetical by name as tiebreaker. Then group by department.
  const sortedStaff = [...staff].sort((a, b) => {
    const ao = a.display_order ?? 100;
    const bo = b.display_order ?? 100;
    if (ao !== bo) return ao - bo;
    return (a.name || '').localeCompare(b.name || '');
  });
  const grouped = sortedStaff.reduce((acc, s) => {
    const key = s.department || 'General';
    (acc[key] = acc[key] || []).push(s);
    return acc;
  }, {});
  // Leadership first if present, then other departments alphabetically
  const departmentOrder = Object.keys(grouped).sort((a, b) => {
    if (a === 'Leadership') return -1;
    if (b === 'Leadership') return 1;
    return a.localeCompare(b);
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-emerald-50">
      <Helmet>
        <title>Our Staff | Kioo Radio 98.1FM</title>
        <meta name="description" content="Meet the dedicated team behind Kioo Radio 98.1FM — broadcasting hope across Liberia, Sierra Leone, and Guinea." />
      </Helmet>

      {/* Scoped styles: B&W portrait + color-on-hover/focus + gentle retouch.
          Images use object-cover with object-position anchored at the TOP and a
          modest zoom. The transform-origin at 0% top means scaling pushes content
          downward only — the top of every head stays inside the frame. */}
      <style>{`
        .staff-card .staff-frame {
          position: relative;
          overflow: hidden;
        }
        .staff-card .staff-photo {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: 50% 0%;
          filter: grayscale(100%) contrast(1.08) saturate(0.95) brightness(1.02);
          transform: scale(1.22);
          transform-origin: 50% 0%;
          transition: transform 500ms ease, filter 500ms ease;
        }
        .staff-card:hover .staff-photo,
        .staff-card:focus-within .staff-photo,
        .staff-card.is-active .staff-photo {
          filter: grayscale(0%) contrast(1.05) saturate(1.08) brightness(1.02);
          transform: scale(1.3);
        }
        @media (prefers-reduced-motion: reduce) {
          .staff-card .staff-photo,
          .staff-card:hover .staff-photo,
          .staff-card:focus-within .staff-photo,
          .staff-card.is-active .staff-photo {
            transform: scale(1.22);
            transition: filter 300ms ease;
          }
        }
      `}</style>

      {/* Hero */}
      <section className="relative bg-gradient-to-r from-kioo-primary via-kioo-secondary to-emerald-700 text-white py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }}></div>
        <div className="relative max-w-5xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-emerald-100 text-sm mb-5">
            <span className="w-2 h-2 bg-emerald-300 rounded-full animate-pulse"></span>
            Our People
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">Meet Our Team</h1>
          <p className="text-lg md:text-xl text-green-100 max-w-3xl mx-auto leading-relaxed">
            The dedicated people behind Kioo Radio 98.1FM — serving listeners across the Makona River Region of Liberia, Sierra Leone, and Guinea.
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4">
          {loading && (
            <div className="text-center py-16 text-gray-500" data-testid="staff-loading">Loading team…</div>
          )}

          {error && !loading && (
            <div className="text-center py-16 text-red-600" data-testid="staff-error">{error}</div>
          )}

          {!loading && !error && staff.length === 0 && (
            <div className="text-center py-16 text-gray-500" data-testid="staff-empty">
              Our staff profiles are coming soon. Please check back shortly.
            </div>
          )}

          {!loading && !error && departmentOrder.map(dept => (
            <div key={dept} className="mb-12" data-testid={`staff-department-${dept.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-0.5 flex-1 bg-gradient-to-r from-transparent to-emerald-300"></div>
                <h2 className="text-2xl md:text-3xl font-bold text-kioo-primary whitespace-nowrap">{dept}</h2>
                <div className="h-0.5 flex-1 bg-gradient-to-l from-transparent to-emerald-300"></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {grouped[dept].map(member => (
                  <article
                    key={member.id}
                    data-testid={`staff-card-${member.id}`}
                    className={`staff-card group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 cursor-pointer ${activeId === member.id ? 'is-active' : ''}`}
                    tabIndex={0}
                    onClick={() => setActiveId(prev => (prev === member.id ? null : member.id))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setActiveId(prev => (prev === member.id ? null : member.id));
                      }
                    }}
                  >
                    <div className="staff-frame relative aspect-[3/4] overflow-hidden bg-gradient-to-br from-emerald-100 to-emerald-50">
                      <img
                        src={member.photo_url ? (member.photo_url.startsWith('http') ? member.photo_url : `${BACKEND_URL}${member.photo_url}`) : PLACEHOLDER}
                        alt={member.name}
                        className="staff-photo"
                        onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
                      />
                      {/* Subtle top vignette for extra contrast without darkening the face */}
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/5" />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 pt-10">
                        <h3 className="text-white text-xl font-bold drop-shadow">{member.name}</h3>
                        <p className="text-emerald-200 text-sm">{member.role}</p>
                      </div>
                    </div>
                    {(member.bio || member.email || member.phone) && (
                      <div className="p-5">
                        {member.bio && (
                          <p className="text-sm text-gray-600 leading-relaxed line-clamp-4 mb-3">{member.bio}</p>
                        )}
                        <div className="flex flex-col gap-1 text-sm">
                          {member.email && (
                            <a href={`mailto:${member.email}`} className="text-kioo-primary hover:underline truncate">
                              ✉️ {member.email}
                            </a>
                          )}
                          {member.phone && (
                            <a href={`tel:${member.phone}`} className="text-kioo-primary hover:underline">
                              📞 {member.phone}
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Staff;
